import { useContext, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams, useNavigate } from "react-router-dom";
import Layout from "../../../layouts/Layout";
import { AuthContext } from "../../../context";

// ─── Star rating (Q8 only) ────────────────────────────────────────────────────
const StarRating = ({ value, onChange }) => (
    <div className="flex gap-2 mt-3">
        {[1, 2, 3, 4, 5].map((star) => (
            <button
                key={star}
                type="button"
                onClick={() => onChange(star)}
                className={`text-2xl transition-colors leading-none ${
                    star <= value ? "text-amber-400" : "text-gray-300 hover:text-amber-200"
                }`}
            >
                ★
            </button>
        ))}
    </div>
);

// ─── Linear scale 1–5 with left/right labels (Q4–Q7) ─────────────────────────
const LinearScale = ({ value, onChange, minLabel, maxLabel }) => (
    <div className="mt-4">
        <div className="flex items-center gap-3">
            <span className="text-xs text-slate-500 text-right shrink-0 w-32">{minLabel}</span>
            <div className="flex gap-6">
                {[1, 2, 3, 4, 5].map((n) => (
                    <div key={n} className="flex flex-col items-center gap-1.5">
                        <span className="text-xs text-slate-500">{n}</span>
                        <input
                            type="radio"
                            checked={value === n}
                            onChange={() => onChange(n)}
                            className="w-4 h-4 accent-indigo-600 cursor-pointer"
                        />
                    </div>
                ))}
            </div>
            <span className="text-xs text-slate-500 shrink-0 w-32">{maxLabel}</span>
        </div>
    </div>
);

// ─── Radio group (Q1–Q3) ─────────────────────────────────────────────────────
const RadioGroup = ({ options, value, onChange }) => (
    <div className="mt-3 space-y-2.5">
        {options.map((opt) => (
            <label key={opt} className="flex items-center gap-2.5 cursor-pointer group">
                <input
                    type="radio"
                    checked={value === opt}
                    onChange={() => onChange(opt)}
                    className="w-4 h-4 accent-indigo-600 cursor-pointer"
                />
                <span className="text-sm text-slate-700 group-hover:text-slate-900">{opt}</span>
            </label>
        ))}
    </div>
);

// ─── Question card wrapper ────────────────────────────────────────────────────
const QuestionCard = ({ label, required, error, errorText, children }) => (
    <div className={`bg-white rounded-xl border shadow-sm px-6 py-5 transition ${
        error ? "border-red-300" : "border-gray-100"
    }`}>
        <p className="text-sm font-medium text-slate-800 leading-snug">
            {label}
            {required && <span className="text-red-500 ml-1">*</span>}
        </p>
        {children}
        {error && <p className="text-xs text-red-500 mt-2">{errorText}</p>}
    </div>
);

// ─── Main form ────────────────────────────────────────────────────────────────
const AuraFeedbackForm = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const { t } = useTranslation();
    const { aiChatBaseUrl, aiChatAuth } = useContext(AuthContext);

    const sessionId = searchParams.get("session_id") || localStorage.getItem("aura_last_session_id") || "";
    const chatId = searchParams.get("chat_id") || "";

    const [form, setForm] = useState({
        answered_question: "",
        satisfactory_first_try: "",
        good_length_and_structure: "",
        relevance_score: 0,
        helpfulness_score: 0,
        clarity_score: 0,
        language_score: 0,
        overall_rating: 0,
        comments: "",
    });

    const [errors, setErrors] = useState({});
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);

    const set = (field, value) => {
        setForm((prev) => ({ ...prev, [field]: value }));
        setErrors((prev) => ({ ...prev, [field]: false }));
    };

    const validate = () => {
        const e = {};
        if (!form.answered_question)       e.answered_question = true;
        if (!form.satisfactory_first_try)  e.satisfactory_first_try = true;
        if (!form.good_length_and_structure) e.good_length_and_structure = true;
        if (!form.relevance_score)         e.relevance_score = true;
        if (!form.helpfulness_score)       e.helpfulness_score = true;
        if (!form.clarity_score)           e.clarity_score = true;
        if (!form.overall_rating)          e.overall_rating = true;
        setErrors(e);
        return Object.keys(e).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validate()) return;
        setSubmitting(true);
        try {
            await fetch(`${aiChatBaseUrl}/feedback/survey`, {
                method: "POST",
                headers: { "Content-Type": "application/json", Authorization: aiChatAuth },
                body: JSON.stringify({
                    session_id: sessionId,
                    chat_id: chatId,
                    ...form,
                }),
            });
        } catch {
            // proceed to success regardless
        } finally {
            setSubmitting(false);
            setSubmitted(true);
        }
    };

    const handleClear = () => {
        setForm({
            answered_question: "",
            satisfactory_first_try: "",
            good_length_and_structure: "",
            relevance_score: 0,
            helpfulness_score: 0,
            clarity_score: 0,
            language_score: 0,
            overall_rating: 0,
            comments: "",
        });
        setErrors({});
    };

    // ── Success screen ──────────────────────────────────────────────────────
    if (submitted) {
        return (
            <Layout>
                <div className="min-h-screen flex items-center justify-center bg-[#EBF8FA] px-4">
                    <div className="bg-white rounded-2xl shadow-lg px-10 py-12 max-w-sm text-center border-t-8 border-teal-500">
                        <div className="text-4xl mb-4">🎉</div>
                        <h2 className="text-lg font-semibold text-slate-900 mb-2">
                            {t('aura survey success title')}
                        </h2>
                        <p className="text-sm text-slate-500 mb-6">
                            {t('aura survey success message')}
                        </p>
                        <button
                            type="button"
                            onClick={() => navigate("/aura-ai-assistant")}
                            className="px-6 py-2.5 rounded-full bg-teal-600 text-white text-sm font-medium hover:bg-teal-700"
                        >
                            {t('aura survey back')}
                        </button>
                    </div>
                </div>
            </Layout>
        );
    }

    // ── Form ────────────────────────────────────────────────────────────────
    return (
        <Layout>
            <div className="min-h-screen bg-[#EBF8FA] px-4 py-8">
                <div className="max-w-2xl mx-auto space-y-4">

                    {/* Header card */}
                    <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
                        <div className="h-2 bg-teal-500 w-full" />
                        <div className="px-6 py-6">
                            <h1 className="text-2xl font-semibold text-slate-900">
                                {t('aura survey title')}
                            </h1>
                            <p className="text-sm font-medium text-teal-600 mt-1">
                                {t('aura survey subtitle')}
                            </p>
                            <p className="text-sm text-slate-600 mt-3 leading-relaxed">
                                {t('aura survey description')}
                            </p>
                            <p className="text-sm text-slate-600 mt-2 leading-relaxed">
                                {t('aura survey impact')}
                            </p>
                            <p className="text-sm text-slate-600 mt-2">
                                {t('aura survey thanks')}
                            </p>
                            <p className="text-xs text-red-500 mt-4">{t('aura survey required legend')}</p>
                        </div>
                    </div>

                    <form onSubmit={handleSubmit} className="space-y-4">

                        {/* Q1 */}
                        <QuestionCard
                            label={t('aura survey q1')}
                            required
                            error={errors.answered_question}
                            errorText={t('aura survey required error')}
                        >
                            <RadioGroup
                                options={[t('aura survey yes'), t('aura survey no'), t('aura survey partial')]}
                                value={form.answered_question}
                                onChange={(v) => set("answered_question", v)}
                            />
                        </QuestionCard>

                        {/* Q2 */}
                        <QuestionCard
                            label={t('aura survey q2')}
                            required
                            error={errors.satisfactory_first_try}
                            errorText={t('aura survey required error')}
                        >
                            <RadioGroup
                                options={[t('aura survey yes'), t('aura survey no')]}
                                value={form.satisfactory_first_try}
                                onChange={(v) => set("satisfactory_first_try", v)}
                            />
                        </QuestionCard>

                        {/* Q3 */}
                        <QuestionCard
                            label={t('aura survey q3')}
                            required
                            error={errors.good_length_and_structure}
                            errorText={t('aura survey required error')}
                        >
                            <RadioGroup
                                options={[t('aura survey yes'), t('aura survey no')]}
                                value={form.good_length_and_structure}
                                onChange={(v) => set("good_length_and_structure", v)}
                            />
                        </QuestionCard>

                        {/* Q4 */}
                        <QuestionCard
                            label={t('aura survey q4')}
                            required
                            error={errors.relevance_score}
                            errorText={t('aura survey required error')}
                        >
                            <LinearScale
                                value={form.relevance_score}
                                onChange={(v) => set("relevance_score", v)}
                                minLabel={t('aura survey q4 min')}
                                maxLabel={t('aura survey q4 max')}
                            />
                        </QuestionCard>

                        {/* Q5 */}
                        <QuestionCard
                            label={t('aura survey q5')}
                            required
                            error={errors.helpfulness_score}
                            errorText={t('aura survey required error')}
                        >
                            <LinearScale
                                value={form.helpfulness_score}
                                onChange={(v) => set("helpfulness_score", v)}
                                minLabel={t('aura survey q5 min')}
                                maxLabel={t('aura survey q5 max')}
                            />
                        </QuestionCard>

                        {/* Q6 */}
                        <QuestionCard
                            label={t('aura survey q6')}
                            required
                            error={errors.clarity_score}
                            errorText={t('aura survey required error')}
                        >
                            <LinearScale
                                value={form.clarity_score}
                                onChange={(v) => set("clarity_score", v)}
                                minLabel={t('aura survey q6 min')}
                                maxLabel={t('aura survey q6 max')}
                            />
                        </QuestionCard>

                        {/* Q7 — optional */}
                        <QuestionCard label={t('aura survey q7')}>
                            <LinearScale
                                value={form.language_score}
                                onChange={(v) => set("language_score", v)}
                                minLabel={t('aura survey q7 min')}
                                maxLabel={t('aura survey q7 max')}
                            />
                        </QuestionCard>

                        {/* Q8 — star rating */}
                        <QuestionCard
                            label={t('aura survey q8')}
                            required
                            error={errors.overall_rating}
                            errorText={t('aura survey required error')}
                        >
                            <StarRating
                                value={form.overall_rating}
                                onChange={(v) => set("overall_rating", v)}
                            />
                        </QuestionCard>

                        {/* Q9 — optional */}
                        <QuestionCard label={t('aura survey q9')}>
                            <input
                                type="text"
                                value={form.comments}
                                onChange={(e) => set("comments", e.target.value)}
                                placeholder={t('aura survey q9 placeholder')}
                                className="w-full mt-3 border-b border-gray-300 focus:border-teal-500
                                focus:outline-none text-sm text-slate-700 bg-transparent
                                placeholder:text-slate-300 pb-1"
                            />
                        </QuestionCard>

                        {/* Actions */}
                        <div className="flex items-center justify-between pt-2 pb-6">
                            <button
                                type="submit"
                                disabled={submitting}
                                className="px-8 py-2.5 rounded-md bg-teal-600 text-white text-sm font-medium
                                hover:bg-teal-700 disabled:opacity-50 shadow-sm"
                            >
                                {submitting ? t('aura survey submitting') : t('aura survey submit')}
                            </button>
                            <button
                                type="button"
                                onClick={handleClear}
                                className="text-sm text-teal-600 hover:text-teal-800"
                            >
                                {t('aura survey clear')}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </Layout>
    );
};

export default AuraFeedbackForm;
